// Admin copy of the web app's src/Organizer/AwardsTable/AwardsTableComponent.jsx (card f65ae884).
// Keep the two in step; only this header, the back link and the import paths differ.
// Organizer Awards Table (card f65ae884; layout approved by Devin 2026-10-03).
// The event-day table where children line up for their medals: type a card
// number or a name, see that dancer's results, tick each award as it is
// handed over, type the next number. Places are the published results
// (GET /award/event/:id); only single-round, single-judge competitions.
// The admin app carries a verbatim copy (back link and imports differ):
// keep the two in step.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import HeaderOrg from '../Headers/HeaderOrg';
import Loader from '../../Components/Loader/Loader';
import { serviceConsumer } from '../../network/ServiceConsumer';
import { formatDate } from '../../helper/formatDate';
import { useMaterialSymbols } from '../OireachtasPanel/symbols';
import { dancerSummary, highlight, orderEntries, ordinal, searchDancers, withCollected } from './awardsSearch';
import '../OireachtasPanel/oireachtas.css';
import '../OireachtasPanel/organizerOireachtas.css';
import './awardsTable.css';

const BACK = (id) => `/feis/manage?id=${id}&Manage=feis`;
const REFRESH_MS = 60 * 1000;
const PICK_LIMIT = 30;

const api = () => process.env.REACT_APP_BASE_URL;
const messageOf = (err) => err?.messages || err?.message || 'Something went wrong. Please try again.';
const statusOf = (err) => err?.error?.response?.status;
const timeLabel = (d) => (d ? d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : '');
const keyOf = (dancerId, competitionId) => `${dancerId}:${competitionId}`;
const nameOf = (d) => (d.missing ? 'Dancer record removed' : `${d.fname} ${d.lname}`.trim() || 'Unnamed dancer');
const isNumber = (q) => /^\d+$/.test(q.trim());
// Material Symbols are ligature text: hidden from screen readers, which
// would otherwise read "trophy 1st" or "check Collected"
const Msr = ({ children }) => (
  <span className="msr" aria-hidden="true">
    {children}
  </span>
);

const StatusPill = ({ dancer }) => {
  const s = dancerSummary(dancer);
  const icon = { todo: 'redeem', done: 'check', wait: 'schedule' }[s.tone];
  return (
    <span className={`awt-pill ${s.tone}`}>
      {icon && <Msr>{icon}</Msr>}
      {s.label}
    </span>
  );
};

const PlacePill = ({ place }) => (
  <span className={`awt-pill place${place <= 3 ? ` p${place}` : ''}`}>
    {place <= 3 && <Msr>trophy</Msr>}
    {ordinal(place)}
  </span>
);

const AwardsTableComponent = () => {
  useMaterialSymbols();
  const navigate = useNavigate();
  const { search } = useLocation();
  const eventId = new URLSearchParams(search).get('id');

  const [data, setData] = useState(null); // { event, competitionsById, dancers }
  const [loadedAt, setLoadedAt] = useState(null);
  const [firstError, setFirstError] = useState('');
  const [refreshError, setRefreshError] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [query, setQuery] = useState('');
  const [openId, setOpenId] = useState(null); // the dancer whose card is open (by id, never by text)
  const [sel, setSel] = useState(0);
  const [busy, setBusy] = useState(() => new Set()); // entry keys with a request in flight
  const [rowErrors, setRowErrors] = useState({});

  const inputRef = useRef(null);
  const pageRef = useRef(null);
  const requestSeq = useRef(0);
  const writes = useRef(0); // ticks started; a refresh that started before one is stale
  const inFlight = useRef(0);
  const refreshWanted = useRef(false); // a refresh asked for while a tick was in flight
  const loadedAtRef = useRef(null);

  // the box keeps the cursor with its text selected: typing the next number replaces it
  const focusSearch = useCallback(() => {
    const el = inputRef.current;
    if (!el) return;
    el.focus();
    el.select();
  }, []);

  const load = useCallback(
    async (first = false) => {
      if (!eventId) return;
      // a refresh never replaces the first load (that one has its own retry)
      if (!first && !loadedAtRef.current) return;
      // never overwrite a tick that is still on its way to the server: run
      // the refresh once the last one is back
      if (!first && inFlight.current > 0) {
        refreshWanted.current = true;
        return;
      }
      refreshWanted.current = false;
      const seq = ++requestSeq.current;
      const writesAtStart = writes.current;
      if (!first) setRefreshing(true);
      try {
        const res = await serviceConsumer('GET', `${api()}/award/event/${eventId}`);
        if (seq !== requestSeq.current || writes.current !== writesAtStart) return;
        setData({
          event: res.event,
          competitionsById: Object.fromEntries((res.competitions || []).map((c) => [String(c._id), c])),
          dancers: res.dancers || [],
        });
        const now = new Date();
        loadedAtRef.current = now;
        setLoadedAt(now);
        setRefreshError(false);
        setFirstError('');
      } catch (err) {
        if (seq !== requestSeq.current) return;
        if (first || !loadedAtRef.current) setFirstError(messageOf(err));
        else setRefreshError(true);
      } finally {
        if (seq === requestSeq.current) setRefreshing(false);
      }
    },
    [eventId]
  );

  useEffect(() => {
    load(true);
  }, [load]);

  // The .oir page area is only as tall as its content; below it the body's
  // own (white) background showed. While this page is open the body takes
  // the page's background, and gets its own back on leaving.
  useEffect(() => {
    const page = pageRef.current;
    if (!page) return undefined;
    const before = document.body.style.backgroundColor;
    document.body.style.backgroundColor = getComputedStyle(page).backgroundColor;
    return () => {
      document.body.style.backgroundColor = before;
    };
  }, []);

  // refresh every minute while the page is on screen, and on coming back to it
  useEffect(() => {
    const stale = () => !loadedAtRef.current || Date.now() - loadedAtRef.current.getTime() >= REFRESH_MS - 1000;
    const tick = () => {
      if (document.visibilityState === 'visible' && stale()) load();
    };
    const timer = setInterval(tick, REFRESH_MS);
    document.addEventListener('visibilitychange', tick);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', tick);
    };
  }, [load]);

  const found = useMemo(() => searchDancers(data?.dancers, query), [data, query]);
  // The open card is held by dancer id, set when the search finds one dancer
  // or one is picked, and kept until the query changes - so a refresh never
  // swaps it for another dancer (a reassigned card, a new name match). A
  // dancer the refresh no longer lists falls back to the search results.
  const opened = useMemo(() => {
    const held = openId && data?.dancers.find((d) => String(d._id) === String(openId));
    if (held) return held;
    return found.results.length === 1 ? found.results[0] : null;
  }, [data, openId, found]);
  // Hold whichever dancer is open by id - including one opened because a
  // load (first load after typing, or a refresh) left a single match - and
  // let go of an id the data no longer has.
  useEffect(() => {
    const next = opened ? String(opened._id) : null;
    if (next !== openId) setOpenId(next);
  }, [opened, openId]);
  const picks = !opened && found.results.length > 1 ? found.results : [];
  const shownPicks = picks.slice(0, PICK_LIMIT);
  // the keyboard selection, kept inside the list a refresh may have shortened
  const selIndex = Math.max(0, Math.min(sel, shownPicks.length - 1));

  const onType = (value) => {
    const single = searchDancers(data?.dancers, value).results;
    setQuery(value);
    setOpenId(single.length === 1 ? String(single[0]._id) : null);
    setSel(0);
    // a refused tick's message belongs to the dancer being served
    setRowErrors({});
  };
  const clear = () => {
    onType('');
    focusSearch();
  };
  const open = (dancer) => {
    setOpenId(String(dancer._id));
    focusSearch();
  };
  const onKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      clear();
    } else if (shownPicks.length && e.key === 'ArrowDown') {
      e.preventDefault();
      setSel(Math.min(selIndex + 1, shownPicks.length - 1));
    } else if (shownPicks.length && e.key === 'ArrowUp') {
      e.preventDefault();
      setSel(Math.max(selIndex - 1, 0));
    } else if (shownPicks.length && e.key === 'Enter') {
      e.preventDefault();
      open(shownPicks[selIndex]);
    }
  };

  // Set (not toggle) one award: shown at once, undone if the server refuses.
  const setCollected = async (dancer, entry, collected) => {
    const key = keyOf(dancer._id, entry.competitionId);
    if (busy.has(key)) return;
    // back to the search box now, ready for the next number (not when the
    // request returns: by then the volunteer may be typing)
    focusSearch();
    writes.current += 1;
    inFlight.current += 1;
    setBusy((s) => new Set(s).add(key));
    setRowErrors(({ [key]: _, ...rest }) => rest);
    setData((d) => ({ ...d, dancers: withCollected(d.dancers, dancer._id, entry.competitionId, collected) }));
    try {
      await serviceConsumer('POST', `${api()}/award/set`, {
        dancerId: String(dancer._id),
        competitionId: String(entry.competitionId),
        collected,
      });
    } catch (err) {
      setData((d) => ({ ...d, dancers: withCollected(d.dancers, dancer._id, entry.competitionId, !collected) }));
      setRowErrors((m) => ({ ...m, [key]: messageOf(err) }));
      // e.g. results unpublished since the last refresh: fetch the current state
      if (statusOf(err) === 409) refreshWanted.current = true;
    } finally {
      inFlight.current -= 1;
      setBusy((s) => {
        const next = new Set(s);
        next.delete(key);
        return next;
      });
      if (inFlight.current === 0 && refreshWanted.current) load();
    }
  };

  const event = data?.event;
  const subtitle = event
    ? [
        event.name,
        event.feis_date &&
          formatDate(event.feis_date).toLocaleDateString('en-CA', { weekday: 'long', month: 'long', day: 'numeric' }),
      ]
        .filter(Boolean)
        .join(' · ')
    : '';

  const renderEntry = (dancer, entry) => {
    const comp = data.competitionsById[String(entry.competitionId)] || {};
    const key = keyOf(dancer._id, entry.competitionId);
    const placed = entry.status === 'placed';
    const pending = entry.status === 'pending';
    // marked collected, but a correction or an unpublish since means no place now
    const mismatch = entry.collected && !placed;
    let pill;
    if (placed) pill = <PlacePill place={entry.place} />;
    else if (pending)
      pill = (
        <span className="awt-pill pending">
          <Msr>schedule</Msr>
          <span className="awt-long">Results pending</span>
          <span className="awt-short">Pending</span>
        </span>
      );
    else if (entry.status === 'no_score')
      pill = (
        <span className="awt-pill noplace">
          <span className="awt-long">Did not dance</span>
          <span className="awt-short">No score</span>
        </span>
      );
    else pill = <span className="awt-pill noplace">No place</span>;
    return (
      <React.Fragment key={key}>
        <div className={`awt-entry${placed || pending ? '' : ' grey'}`}>
          <span className="awt-code">{comp.code}</span>
          <span className="awt-desc">{comp.description}</span>
          {pill}
          {placed || mismatch ? (
            <button
              type="button"
              className={`awt-collect${entry.collected ? ' on' : ''}`}
              aria-label={`${comp.code} collected`}
              aria-pressed={entry.collected}
              disabled={busy.has(key)}
              onClick={() => setCollected(dancer, entry, !entry.collected)}
            >
              {entry.collected ? (
                <>
                  <Msr>check</Msr>Collected
                </>
              ) : (
                'Mark collected'
              )}
            </button>
          ) : (
            <span />
          )}
        </div>
        {mismatch && (
          <div className="awt-rowmsg">
            <Msr>error</Msr>
            {pending
              ? `${comp.code} is marked collected, but its results are not published now. Recover the award or untick it.`
              : `${comp.code} is marked collected, but after a correction this dancer no longer places. Recover the award or untick it.`}
          </div>
        )}
        {rowErrors[key] && (
          <div className="awt-rowmsg" role="alert">
            <Msr>error</Msr>
            {rowErrors[key]}
          </div>
        )}
      </React.Fragment>
    );
  };

  const renderDancer = (dancer) => (
    <>
      {openId && found.results.length > 1 && (
        <button
          type="button"
          className="op-back awt-back"
          onClick={() => {
            setOpenId(null);
            focusSearch();
          }}
        >
          <Msr>arrow_back</Msr>Back to {found.results.length} matches
        </button>
      )}
      <div className="awt-dancer">
        <div className="awt-card-n">
          <small>Card</small>
          {dancer.card ?? '—'}
        </div>
        <div>
          <div className="awt-name">{nameOf(dancer)}</div>
          {dancer.school && <div className="awt-school">{dancer.school}</div>}
          <div className="awt-status">
            <StatusPill dancer={dancer} />
          </div>
        </div>
        <div className="awt-entries">
          {orderEntries(dancer.entries, data.competitionsById).map((e) => renderEntry(dancer, e))}
        </div>
      </div>
    </>
  );

  const renderPicks = () => {
    const [first] = query.trim().split(/\s+/);
    return (
      <div className="awt-picks">
        <div className="awt-picks-h">
          {found.kind === 'card'
            ? `${picks.length} dancers have card ${query.trim()} - pick one.`
            : `${picks.length} dancers match “${query.trim()}” - pick one, or keep typing.`}
        </div>
        <div role="listbox" id="awt-picks" aria-label="Matching dancers">
          {shownPicks.map((d, i) => {
            const [a, b, c] = highlight(nameOf(d), first);
            return (
              <button
                type="button"
                key={String(d._id)}
                id={`awt-pick-${i}`}
                role="option"
                aria-selected={i === selIndex}
                className={`awt-pick${i === selIndex ? ' sel' : ''}`}
                onMouseEnter={() => setSel(i)}
                onClick={() => open(d)}
              >
                <span className="awt-pick-c">{d.card ?? '—'}</span>
                <span>
                  <div className="awt-pick-n">
                    {a}
                    {b && <mark>{b}</mark>}
                    {c}
                  </div>
                  {d.school && <div className="awt-pick-s">{d.school}</div>}
                </span>
                <span className="awt-pick-st">
                  <StatusPill dancer={d} />
                </span>
                <Msr>chevron_right</Msr>
              </button>
            );
          })}
        </div>
        {picks.length > PICK_LIMIT && (
          <div className="awt-more">
            Showing {PICK_LIMIT} of {picks.length}. Keep typing to narrow it down.
          </div>
        )}
      </div>
    );
  };

  const renderEmpty = (icon, title, sub, action) => (
    <div className="awt-empty">
      <Msr>{icon}</Msr>
      <div className="awt-empty-t">{title}</div>
      {sub && <div className="awt-empty-s">{sub}</div>}
      {action}
    </div>
  );

  let body = null;
  if (!eventId) body = renderEmpty('error', 'No event selected', 'Open the Awards Table from the event’s manage page.');
  else if (!data && firstError)
    body = renderEmpty(
      'cloud_off',
      'Could not load the Awards Table',
      firstError,
      <button type="button" className="op-btn" onClick={() => load(true)}>
        <Msr>refresh</Msr>Try again
      </button>
    );
  else if (data) {
    const q = query.trim();
    if (opened) body = renderDancer(opened);
    else if (found.kind === 'empty')
      body = renderEmpty('badge', 'Enter a card number or name', `${data.dancers.length} dancers loaded · results update every minute`);
    else if (found.kind === 'none')
      body = isNumber(q)
        ? renderEmpty('search_off', `No dancer with card ${q}`, 'Check the number, or search by name.')
        : renderEmpty('search_off', `No dancer matches “${q}”`, 'Check the spelling, or search by card number.');
    else body = renderPicks();
  }

  return (
    <>
      <HeaderOrg />
      <div className="oir op-page awt-page" ref={pageRef}>
        {!data && !firstError && eventId && <Loader />}
        <div className="wrap">
          <button type="button" className="op-back" onClick={() => navigate(BACK(eventId))}>
            <Msr>arrow_back</Msr>Manage event
          </button>
          <div className="awt-title" role="heading" aria-level={1}>Awards Table</div>
          <div className="awt-sub">{subtitle || ' '}</div>

          <div className="awt-bar">
            <label className="awt-search">
              <Msr>search</Msr>
              <input
                ref={inputRef}
                type="text"
                value={query}
                autoFocus
                autoComplete="off"
                spellCheck={false}
                placeholder="Card number or name"
                aria-label="Card number or name"
                aria-controls={shownPicks.length ? 'awt-picks' : undefined}
                aria-activedescendant={shownPicks.length ? `awt-pick-${selIndex}` : undefined}
                onChange={(e) => onType(e.target.value)}
                onKeyDown={onKeyDown}
              />
              {query && (
                <button type="button" className="awt-clear" title="Clear (Esc)" aria-label="Clear search" onClick={clear}>
                  <Msr>close</Msr>
                </button>
              )}
            </label>
            <button
              type="button"
              className="op-btn awt-refresh"
              title="Refresh"
              aria-label="Refresh"
              disabled={refreshing || !data}
              onClick={() => load()}
            >
              <Msr>refresh</Msr>
              <span className="awt-lbl">Refresh</span>
            </button>
          </div>
          <div className="awt-hint">
            <span>Type a card number or a name. Tick each award as you hand it over, then type the next number - the box stays ready.</span>
            {loadedAt && (
              <span className={`awt-upd${refreshError ? ' awt-stale' : ''}`}>
                {refreshError
                  ? `Couldn’t refresh - showing results from ${timeLabel(loadedAt)}`
                  : `Updated ${timeLabel(loadedAt)} · refreshes every minute`}
              </span>
            )}
          </div>

          {body}
        </div>
      </div>
    </>
  );
};

export default AwardsTableComponent;
